/**
 * One flag, and the questions worth asking about it.
 *
 * The checkbox is hand-drawn rather than the design system's: the mockup puts
 * an 18px square beside multi-line text with the label doing the hit-testing,
 * and the DS Checkbox lays its label out on one line beside the box. Note the
 * DS component's other trap while you are here — its `onChange` is
 * `(nextValue, event)`, not a change event (web/src/ds.ts).
 */

import { useState } from 'react';
import { ds } from '../ds';
import { EASE, FLAG_LABEL, RADIUS, mono, monoSmall } from '../theme';
import type { DetailFlag } from '../api';

interface Props {
  flag: DetailFlag;
  onToggle: (questionId: string, checked: boolean) => void;
  onAdd: (text: string, flagId: string | null) => Promise<void>;
}

export function FlagCard({ flag, onToggle, onAdd }: Props) {
  const { Button, Icon, Input } = ds();
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);

  const add = async () => {
    const text = draft.trim();
    if (!text) return;
    setAdding(true);
    await onAdd(text, flag.id);
    setDraft('');
    setAdding(false);
  };

  return (
    <div style={{ padding: '22px 0', borderTop: '1px solid var(--border-subtle)' }}>
      {flag.tag && (
        <div style={{ ...monoSmall, color: 'var(--accent)', marginBottom: 8 }}>
          {FLAG_LABEL[flag.tag] ?? flag.tag}
        </div>
      )}
      {flag.title && (
        <div
          style={{
            fontFamily: "var(--font-display), 'Baloo 2', system-ui",
            fontWeight: 800,
            fontSize: 20,
            lineHeight: 1.15,
            color: 'var(--text-strong)',
            marginBottom: 8,
            textWrap: 'pretty'
          }}
        >
          {flag.title}
        </div>
      )}
      {flag.body && (
        <p style={{ margin: '0 0 14px', fontSize: 14, lineHeight: 1.6, color: 'var(--text-muted)', textWrap: 'pretty' }}>
          {flag.body}
        </p>
      )}
      {flag.quote && (
        <div style={{ padding: '10px 0 10px 14px', borderLeft: '2px solid var(--violet-800)', marginBottom: 16 }}>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: 'var(--text-faint)', fontStyle: 'italic', textWrap: 'pretty' }}>
            &ldquo;{flag.quote}&rdquo;
          </p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {flag.questions.map((q) => {
          const sent = q.sentAt !== null;
          return (
            <label
              key={q.id}
              className={sent ? undefined : 'mv-row'}
              style={{
                display: 'grid',
                gridTemplateColumns: '18px minmax(0,1fr)',
                gap: 12,
                padding: '9px 10px 9px 8px',
                marginLeft: -8,
                borderRadius: RADIUS.chip,
                cursor: sent ? 'default' : 'pointer',
                opacity: sent ? 0.6 : 1,
                transition: `background 120ms ${EASE}`
              }}
            >
              <input
                type="checkbox"
                checked={q.checked}
                disabled={sent}
                onChange={(e) => onToggle(q.id, e.target.checked)}
                style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
              />
              <span
                aria-hidden="true"
                style={{
                  width: 18,
                  height: 18,
                  marginTop: 2,
                  borderRadius: 6,
                  boxSizing: 'border-box',
                  background: q.checked ? 'var(--primary)' : 'transparent',
                  border: q.checked ? 'none' : '1px solid var(--border-strong)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {q.checked && <Icon name="check" size={12} color="var(--text-on-primary)" />}
              </span>
              <span style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--text-body)', textWrap: 'pretty' }}>
                {q.text}
                {sent && <span style={{ ...mono, fontSize: 9, marginLeft: 8 }}>Sent</span>}
              </span>
            </label>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add your own question"
          />
        </div>
        <Button variant="soft" size="md" onClick={() => void add()} disabled={!draft.trim() || adding}>
          Add
        </Button>
      </div>
    </div>
  );
}
